import dayjs from 'dayjs';
import JSZip from 'jszip';
import { resolveUploadAssetUrl } from './resolveUploadAssetUrl';

function sanitizeFilenamePart(s: string, fallback: string): string {
  const t = String(s || '').trim() || fallback;
  return t
    .replace(/[/\\:*?"<>|]/g, '_')
    .replace(/\s+/g, '_')
    .slice(0, 80);
}

function toAbsoluteFetchUrl(resolved: string): string {
  if (/^https?:\/\//i.test(resolved)) return resolved;
  if (resolved.startsWith('/')) return `${window.location.origin}${resolved}`;
  return `${window.location.origin}/${resolved}`;
}

function extFromPathOrUrl(url: string): string {
  const clean = url.split('?')[0] || '';
  const m = clean.match(/\.([a-zA-Z0-9]{1,8})$/);
  return m ? `.${m[1].toLowerCase()}` : '.jpg';
}

export type ProcurementExportRow = {
  /** CSV 一行（已按列拼好，含引号） */
  csvCells: string[];
  /** 行标识，用于照片子目录名 */
  rowKey: string;
  receiptPhotoUrls?: string[];
};

/**
 * 导出采购订单：CSV + 收货照片目录打包为 zip。
 * 无收货图时仍生成仅含 CSV 的 zip，便于统一下载体验。
 */
export async function downloadProcurementExportZip(opts: {
  filePrefix: string;
  csvHeaders: string[];
  rows: ProcurementExportRow[];
}): Promise<void> {
  const { filePrefix, csvHeaders, rows } = opts;
  if (!rows.length) {
    throw new Error('没有可导出的数据');
  }

  const zip = new JSZip();
  const csvContent = [
    csvHeaders.join(','),
    ...rows.map((r) => r.csvCells.join(','))
  ].join('\n');
  zip.file('采购列表.csv', '\ufeff' + csvContent);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  let photoCount = 0;

  for (const row of rows) {
    const urls = (row.receiptPhotoUrls || []).filter(Boolean);
    if (!urls.length) continue;
    const folderName = sanitizeFilenamePart(row.rowKey, 'order');
    for (let i = 0; i < urls.length; i++) {
      const url = urls[i];
      const resolved = resolveUploadAssetUrl(url) || url;
      const fetchUrl = toAbsoluteFetchUrl(resolved);
      try {
        const res = await fetch(fetchUrl, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (!res.ok) continue;
        const blob = await res.blob();
        const ext = extFromPathOrUrl(url);
        zip.file(
          `收货照片/${folderName}/${String(i + 1).padStart(2, '0')}_收货图${ext}`,
          blob
        );
        photoCount += 1;
      } catch {
        /* skip failed image */
      }
    }
  }

  const stamp = dayjs().format('YYYYMMDD_HHmmss');
  const zipName = `${sanitizeFilenamePart(filePrefix, '采购导出')}_${stamp}.zip`;
  const out = await zip.generateAsync({ type: 'blob' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(out);
  a.download = zipName;
  a.click();
  URL.revokeObjectURL(a.href);

  if (photoCount === 0 && rows.some((r) => (r.receiptPhotoUrls || []).length > 0)) {
    console.warn('[procurement-export] 部分收货图未能打入 zip');
  }
}
